"use client";

import React, { useState, useEffect } from "react";
import {
  Container,
  Grid,
  Typography,
  Card,
  CardContent,
  Button,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  IconButton,
  useTheme,
  alpha,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Rating,
  Snackbar,
} from "@mui/material";
import {
  Close,
  Download,
  Description,
  TrendingUp,
  ShoppingCart,
  Business,
  CheckCircle,
  // DescriptionOutlined,
  Preview,
} from "@mui/icons-material";
import {
  collection,
  getDocs,
  query,
  where,
  orderBy,
  addDoc,
  updateDoc,
  doc,
  increment,
  limit,
} from "firebase/firestore";
import { db } from "@/lib/firebase";
import { Whitepaper } from "@/types/resources";

export const runtime = "edge";

// Icon mapping
const iconMap = {
  TrendingUp: <TrendingUp sx={{ fontSize: 40 }} />,
  Description: <Description sx={{ fontSize: 40 }} />,
  ShoppingCart: <ShoppingCart sx={{ fontSize: 40 }} />,
  Business: <Business sx={{ fontSize: 40 }} />,
};

const fetchWhitepapers = async (): Promise<Whitepaper[]> => {
  try {
    const q = query(
      collection(db, "whitepapers"),
      where("isActive", "==", true),
      orderBy("downloadCount", "desc")
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdAt:
        doc.data().createdAt?.toDate?.()?.toISOString() ||
        new Date().toISOString(),
      updatedAt:
        doc.data().updatedAt?.toDate?.()?.toISOString() ||
        new Date().toISOString(),
      lastDownloadedAt:
        doc.data().lastDownloadedAt?.toDate?.()?.toISOString() ||
        new Date().toISOString(),
    })) as Whitepaper[];
  } catch (error) {
    console.error("Error fetching whitepapers:", error);
    return [];
  }
};

const fetchRecommendedWhitepapers = async (
  currentWhitepaperId: string
): Promise<Whitepaper[]> => {
  try {
    const q = query(
      collection(db, "whitepapers"),
      where("isActive", "==", true),
      where("id", "!=", currentWhitepaperId),
      orderBy("downloadCount", "desc"),
      limit(3)
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    })) as Whitepaper[];
  } catch (error) {
    console.error("Error fetching recommended whitepapers:", error);
    return [];
  }
};

// Reuse the same utility functions from Tools page
const checkDownloadEligibility = async (
  email: string,
  resourceId: string
): Promise<boolean> => {
  try {
    const q = query(
      collection(db, "downloads"),
      where("email", "==", email),
      where("resourceId", "==", resourceId),
      where(
        "downloadedAt",
        ">",
        new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString()
      )
    );
    const snapshot = await getDocs(q);
    return snapshot.empty;
  } catch (error) {
    console.error("Error checking download eligibility:", error);
    return true;
  }
};

const recordDownload = async (
  resourceId: string,
  userData: { name: string; email: string; company?: string },
  resourceType: "tool" | "whitepaper"
) => {
  try {
    await addDoc(collection(db, "downloads"), {
      resourceId,
      resourceType,
      ...userData,
      downloadedAt: new Date().toISOString(),
      success: true,
    });

    const resourceRef = doc(
      db,
      resourceType === "tool" ? "tools" : "whitepapers",
      resourceId
    );
    await updateDoc(resourceRef, {
      downloadCount: increment(1),
      lastDownloadedAt: new Date().toISOString(),
    });

    return true;
  } catch (error) {
    console.error("Error recording download:", error);

    try {
      await addDoc(collection(db, "download_attempts"), {
        resourceId,
        resourceType,
        ...userData,
        attemptedAt: new Date().toISOString(),
        success: false,
        error: error instanceof Error ? error.message : "Unknown error",
      });
    } catch (logError) {
      console.error("Error logging download attempt:", logError);
    }

    throw error;
  }
};

// Simple time formatter without date-fns
const formatTimeAgo = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return "just now";
    if (diffInSeconds < 3600)
      return `${Math.floor(diffInSeconds / 60)} minutes ago`;
    if (diffInSeconds < 86400)
      return `${Math.floor(diffInSeconds / 3600)} hours ago`;
    if (diffInSeconds < 2592000)
      return `${Math.floor(diffInSeconds / 86400)} days ago`;
    if (diffInSeconds < 31536000)
      return `${Math.floor(diffInSeconds / 2592000)} months ago`;
    return `${Math.floor(diffInSeconds / 31536000)} years ago`;
  } catch {
    return "recently";
  }
};

export default function Whitepapers() {
  const theme = useTheme();
  const [whitepapers, setWhitepapers] = useState<Whitepaper[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedWhitepaper, setSelectedWhitepaper] =
    useState<Whitepaper | null>(null);
  const [previewWhitepaper, setPreviewWhitepaper] = useState<Whitepaper | null>(
    null
  );
  const [recommendedWhitepapers, setRecommendedWhitepapers] = useState<
    Whitepaper[]
  >([]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
  });
  const [formErrors, setFormErrors] = useState({
    name: "",
    email: "",
  });
  const [earlyInterestEmail, setEarlyInterestEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "" });

  useEffect(() => {
    const loadWhitepapers = async () => {
      try {
        const data = await fetchWhitepapers();
        setWhitepapers(data);
      } catch (error) {
        console.error("Error loading whitepapers:", error);
        setError("Failed to load whitepapers. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    loadWhitepapers();
  }, []);

  useEffect(() => {
    if (submitted && selectedWhitepaper) {
      const loadRecommendations = async () => {
        const recommendations = await fetchRecommendedWhitepapers(
          selectedWhitepaper.id
        );
        setRecommendedWhitepapers(recommendations);
      };
      loadRecommendations();
    }
  }, [submitted, selectedWhitepaper]);

  const validateForm = () => {
    const errors = {
      name: formData.name.trim() ? "" : "Name is required",
      email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)
        ? ""
        : "Valid email is required",
    };
    setFormErrors(errors);
    return !errors.name && !errors.email;
  };

  const validateEarlyInterest = () => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(earlyInterestEmail);
  };

  const handleDownload = (whitepaper: Whitepaper) => {
    setSelectedWhitepaper(whitepaper);
    setSubmitted(false);
    setFormData({ name: "", email: "", company: "" });
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedWhitepaper) return;

    if (!validateForm()) {
      setError("Please fix the form errors before submitting.");
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Check download eligibility
      const isEligible = await checkDownloadEligibility(
        formData.email,
        selectedWhitepaper.id
      );
      if (!isEligible) {
        setError(
          "You've already downloaded this whitepaper in the last 24 hours."
        );
        setSubmitting(false);
        return;
      }

      // 1️⃣ Send data to Airtable
      const airtableResponse = await fetch("/api/submitToAirtable", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          consent: true,
          interest: "Whitepaper & Guide Download",
          resourceTitle: selectedWhitepaper.title,
        }),
      });

      if (!airtableResponse.ok) {
        throw new Error("Failed to submit data to Airtable.");
      }

      // 2️⃣ Record download in Firestore
      await recordDownload(selectedWhitepaper.id, formData, "whitepaper");

      // 3️⃣ Mark submission successful
      setSubmitted(true);

      // 4️⃣ Trigger download with small delay
      setTimeout(() => {
        if (selectedWhitepaper.fileUrl) {
          const link = document.createElement("a");
          link.href = selectedWhitepaper.fileUrl;
          link.download = `${selectedWhitepaper.title}.pdf`;
          link.target = "_blank";
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }, 1500);
    } catch (error) {
      console.error("Error processing submission or download:", error);
      setError("Failed to process your request. Please try again.");
      setSubmitting(false);
    }
  };

  const handleEarlyInterest = async () => {
    if (!validateEarlyInterest()) {
      setSnackbar({
        open: true,
        message: "Please enter a valid email address.",
      });
      return;
    }

    try {
      await fetch("/api/submitToAirtable", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: earlyInterestEmail,
          interest: "Early Access - Whitepapers",
          status: "Early Interest",
        }),
      });

      setEarlyInterestEmail("");
      setSnackbar({
        open: true,
        message: "Thanks! We'll notify you when new whitepapers are available.",
      });
    } catch (error) {
      console.error("Error submitting early interest:", error);
      setSnackbar({
        open: true,
        message: "Failed to submit. Please try again.",
      });
    }
  };

  const handleClose = () => {
    setSelectedWhitepaper(null);
    setSubmitted(false);
    setRecommendedWhitepapers([]);
  };

  const handlePreviewClose = () => {
    setPreviewWhitepaper(null);
  };

  const calculateRating = (downloadCount: number) => {
    const baseRating = 4.7;
    const reviewCount = Math.floor(downloadCount / 15);
    return {
      rating: baseRating + Math.random() * 0.3,
      reviews: reviewCount || 1,
    };
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 8, textAlign: "center" }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Loading whitepapers...</Typography>
      </Container>
    );
  }

  if (!whitepapers.length) {
    return (
      <Container
        maxWidth="lg"
        sx={{
          py: 12,
          textAlign: "center",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background elements... */}

        <Box sx={{ position: "relative", zIndex: 2 }}>
          {/* Existing empty state content... */}

          {/* Enhanced Early Interest Capture */}
          <Box sx={{ mt: 6, maxWidth: 500, mx: "auto" }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
              Get Research Insights First
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 3 }}>
              Be the first to access our cutting-edge research and industry
              insights
            </Typography>

            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Enter your email"
                value={earlyInterestEmail}
                onChange={(e) => setEarlyInterestEmail(e.target.value)}
                error={!!earlyInterestEmail && !validateEarlyInterest()}
                helperText={
                  earlyInterestEmail && !validateEarlyInterest()
                    ? "Please enter a valid email"
                    : ""
                }
              />
              <Button
                variant="contained"
                onClick={handleEarlyInterest}
                disabled={!validateEarlyInterest()}
                sx={{ whiteSpace: "nowrap" }}
              >
                Get Insights
              </Button>
            </Box>

            <Typography variant="caption" color="text.secondary">
              We respect your privacy. No spam, unsubscribe anytime.
            </Typography>
          </Box>
        </Box>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          message={snackbar.message}
        />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Header */}
      <Box textAlign="center" sx={{ mb: 8 }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { xs: "2.5rem", md: "3.5rem" },
            fontWeight: 800,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            backgroundClip: "text",
            WebkitBackgroundClip: "text",
            color: "transparent",
            mb: 3,
          }}
        >
          Whitepapers & Guides
        </Typography>
        <Typography
          variant="h5"
          color="text.secondary"
          sx={{ maxWidth: 600, mx: "auto", lineHeight: 1.6 }}
        >
          In-depth research and insights to help you navigate digital
          transformation, automation, and web development in 2025 and beyond.
        </Typography>
      </Box>

      {/* Whitepapers Grid */}
      <Grid container spacing={4}>
        {whitepapers.map((whitepaper) => {
          const { rating, reviews } = calculateRating(
            whitepaper.downloadCount || 0
          );

          return (
            <Grid size={{ xs: 12, md: 6 }} key={whitepaper.id}>
              <Card
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  transition: "all 0.3s ease",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  "&:hover": {
                    transform: "translateY(-8px)",
                    boxShadow: `0 20px 40px ${alpha(
                      theme.palette.primary.main,
                      0.15
                    )}`,
                  },
                }}
              >
                <CardContent
                  sx={{
                    p: 4,
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", mb: 3 }}
                  >
                    <Box sx={{ color: theme.palette.primary.main, mr: 3 }}>
                      {iconMap[whitepaper.icon as keyof typeof iconMap]}
                    </Box>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="h5" fontWeight={600} gutterBottom>
                        {whitepaper.title}
                      </Typography>

                      {/* Social Proof */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        <Rating value={rating} size="small" readOnly />
                        <Typography variant="body2" color="text.secondary">
                          ({reviews} reviews)
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: 1,
                          mb: 2,
                        }}
                      >
                        {whitepaper.keywords.map((keyword, index) => (
                          <Chip
                            key={index}
                            label={keyword}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: alpha(
                                theme.palette.primary.main,
                                0.3
                              ),
                              color: "text.primary",
                              fontSize: "0.75rem",
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Box>

                  <Typography
                    color="text.secondary"
                    sx={{ mb: 3, flexGrow: 1 }}
                  >
                    {whitepaper.description}
                  </Typography>

                  <List dense sx={{ mb: 3 }}>
                    {whitepaper.contents.slice(0, 3).map((content, index) => (
                      <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircle
                            sx={{
                              fontSize: 16,
                              color: theme.palette.primary.main,
                            }}
                          />
                        </ListItemIcon>
                        <ListItemText primary={content} />
                      </ListItem>
                    ))}
                  </List>

                  {/* Download Count & Recent Activity */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      📥 Downloaded by{" "}
                      {whitepaper.downloadCount?.toLocaleString() || 0}{" "}
                      professionals
                    </Typography>
                    {whitepaper.lastDownloadedAt && (
                      <Typography variant="caption" color="text.secondary">
                        Last download:{" "}
                        {formatTimeAgo(whitepaper.lastDownloadedAt)}
                      </Typography>
                    )}
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    <Button
                      variant="outlined"
                      startIcon={<Preview />}
                      onClick={() => setPreviewWhitepaper(whitepaper)}
                    >
                      Preview
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={<Download />}
                      onClick={() => handleDownload(whitepaper)}
                      sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: `0 8px 25px ${alpha(
                            theme.palette.primary.main,
                            0.3
                          )}`,
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      Download PDF
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Download Dialog */}
      <Dialog
        open={Boolean(selectedWhitepaper)}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            position: "relative",
            fontWeight: 600,
            pr: 8,
          }}
        >
          Download {selectedWhitepaper?.title}
          <IconButton
            onClick={handleClose}
            sx={{ position: "absolute", right: 16, top: 16 }}
            disabled={submitting}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {!submitted ? (
            <>
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Please provide your details to download this exclusive
                whitepaper. We&apos;ll send the PDF directly to your email.
              </Typography>

              <Box component="form" onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      value={formData.name}
                      onChange={(e) => {
                        setFormData({ ...formData, name: e.target.value });
                        setFormErrors({ ...formErrors, name: "" });
                      }}
                      error={!!formErrors.name}
                      helperText={formErrors.name}
                      required
                      disabled={submitting}
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      type="email"
                      value={formData.email}
                      onChange={(e) => {
                        setFormData({ ...formData, email: e.target.value });
                        setFormErrors({ ...formErrors, email: "" });
                      }}
                      error={!!formErrors.email}
                      helperText={formErrors.email}
                      required
                      disabled={submitting}
                    />
                  </Grid>
                  <Grid size={12}>
                    <TextField
                      fullWidth
                      label="Company"
                      value={formData.company}
                      onChange={(e) =>
                        setFormData({ ...formData, company: e.target.value })
                      }
                      disabled={submitting}
                    />
                  </Grid>
                  <Grid size={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      fullWidth
                      size="large"
                      disabled={submitting}
                      sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                        "&:hover": {
                          transform: "translateY(-2px)",
                        },
                        transition: "all 0.3s ease",
                      }}
                    >
                      {submitting ? (
                        <CircularProgress size={24} />
                      ) : (
                        "Download Whitepaper"
                      )}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </>
          ) : (
            <Box textAlign="center" sx={{ py: 4 }}>
              <CheckCircle
                sx={{
                  fontSize: 64,
                  color: theme.palette.success.dark,
                  mb: 2,
                }}
              />
              <Typography variant="h6" gutterBottom>
                Download Started!
              </Typography>
              <Typography color="text.secondary" sx={{ mb: 3 }}>
                Thank you for your interest. The whitepaper is being downloaded
                and we&apos;ve also sent a copy to your email address.
              </Typography>

              <Button
                variant="contained"
                onClick={handleClose}
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
                  mb: 3,
                  "&:hover": {
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                Close
              </Button>

              {/* Recommended Whitepapers */}
              {recommendedWhitepapers.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    You Might Also Like
                  </Typography>
                  <List>
                    {recommendedWhitepapers.map((whitepaper) => (
                      <ListItem
                        key={whitepaper.id}
                        component="div"
                        onClick={() => {
                          setSelectedWhitepaper(whitepaper);
                          setSubmitted(false);
                          setRecommendedWhitepapers([]);
                        }}
                        sx={{
                          borderRadius: 1,
                          mb: 1,
                          cursor: "pointer",
                          "&:hover": {
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.04
                            ),
                          },
                        }}
                      >
                        <ListItemText
                          primary={whitepaper.title}
                          secondary={whitepaper.description}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog
        open={Boolean(previewWhitepaper)}
        onClose={handlePreviewClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            position: "relative",
            fontWeight: 600,
            pr: 8,
          }}
        >
          {previewWhitepaper?.title}
          <IconButton
            onClick={handlePreviewClose}
            sx={{ position: "absolute", right: 16, top: 16 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {previewWhitepaper && (
            <>
              <Typography variant="h6" gutterBottom>
                Table of Contents:
              </Typography>
              <List>
                {previewWhitepaper.contents.map((content, index) => (
                  <ListItem key={index}>
                    <ListItemIcon>
                      <CheckCircle color="primary" />
                    </ListItemIcon>
                    <ListItemText primary={content} />
                  </ListItem>
                ))}
              </List>
              <Button
                variant="contained"
                fullWidth
                onClick={() => {
                  handlePreviewClose();
                  handleDownload(previewWhitepaper);
                }}
                sx={{ mt: 2 }}
                startIcon={<Download />}
              >
                Download Whitepaper
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Container>
  );
}
